import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  MenuBook as ContentIcon,
  FitnessCenter as ExerciseIcon,
  Quiz as QuizIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Refresh as RestartIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setCurrentModule, completeModule, restartModule } from '../../store/progressSlice';
import MermaidDiagram from '../MermaidDiagram';

interface ModuleViewerProps {
  courseId: string;
  moduleId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`module-tabpanel-${index}`}
      aria-labelledby={`module-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ModuleViewer: React.FC<ModuleViewerProps> = ({ courseId, moduleId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentTab, setCurrentTab] = useState(0);

  const course = useAppSelector(state => state.courses[courseId]);
  const progress = useAppSelector(state => state.progress.courseProgress[courseId]);

  const module = course?.modules.find(m => m.id === moduleId);
  const moduleIndex = course?.modules.findIndex(m => m.id === moduleId) ?? -1;
  const isLastModule = moduleIndex === (course?.modules.length ?? 0) - 1;
  const nextModule = !isLastModule && course?.modules[moduleIndex + 1];
  const prevModule = moduleIndex > 0 && course?.modules[moduleIndex - 1];

  const isCompleted = progress?.completedModules.includes(moduleId) || false;
  const completedExercises =
    module?.exercises.filter(ex => progress?.exerciseProgress[ex.id]?.completed).length || 0;

  const completionPercentage = module
    ? Math.round((completedExercises / module.exercises.length) * 100)
    : 0;

  useEffect(() => {
    if (courseId && moduleId) {
      dispatch(setCurrentModule({ courseId, moduleId }));
    }
  }, [courseId, moduleId, dispatch]);

  if (!course || !module) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Typography variant='h4'>Module not found</Typography>
      </Container>
    );
  }

  // Check if module is under development
  if (module.isUnderDevelopment) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant='h4' gutterBottom>
            Module Under Development
          </Typography>
          <Typography variant='body1' color='text.secondary' paragraph>
            Module {moduleIndex + 1}: {module.title} is currently under development.
          </Typography>
          <Typography variant='body2' color='text.secondary' paragraph>
            Please check back later or explore other available modules.
          </Typography>
          <Button
            variant='contained'
            onClick={() => navigate(`/courses/${courseId}`)}
            sx={{ mt: 2 }}
          >
            Back to Course Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleExerciseClick = (exerciseId: string) => {
    navigate(`/courses/${courseId}/modules/${moduleId}/exercises/${exerciseId}`);
  };

  const handleQuizClick = () => {
    navigate(`/courses/${courseId}/modules/${moduleId}/quiz`);
  };

  const handleCompleteModule = () => {
    dispatch(completeModule({ courseId, moduleId }));
  };

  const handleRestartModule = () => {
    dispatch(restartModule({ courseId, moduleId }));
  };

  const handleNextModule = () => {
    if (nextModule) {
      navigate(`/courses/${courseId}/modules/${nextModule.id}`);
    }
  };

  const handlePrevModule = () => {
    if (prevModule) {
      navigate(`/courses/${courseId}/modules/${prevModule.id}`);
    }
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Module Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
        >
          <Typography variant='h3' component='h1'>
            Module {moduleIndex + 1}: {module.title}
          </Typography>

          {/* Restart Module Button */}
          {isCompleted && (
            <Button
              variant='contained'
              color='secondary'
              startIcon={<RestartIcon />}
              onClick={handleRestartModule}
              size='medium'
            >
              Restart Module
            </Button>
          )}
        </Box>

        <Typography variant='h6' color='text.secondary' paragraph>
          {module.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {module.duration && <Chip label={module.duration} variant='outlined' />}
          <Chip label={`${module.exercises.length} exercises`} variant='outlined' />
          {module.quiz && <Chip label='Quiz included' variant='outlined' />}
          {isCompleted && <Chip label='Completed' color='success' />}
        </Box>

        {/* Progress Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant='body2' sx={{ flexGrow: 1 }}>
              Module Progress
            </Typography>
            <Typography variant='body2'>
              {completedExercises} / {module.exercises.length} exercises completed
            </Typography>
          </Box>
          <LinearProgress
            variant='determinate'
            value={completionPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant='fullWidth'>
          <Tab label='Content' icon={<ContentIcon />} iconPosition='start' />
          <Tab
            label={`Exercises (${module.exercises.length})`}
            icon={<ExerciseIcon />}
            iconPosition='start'
          />
          {module.quiz && <Tab label='Quiz' icon={<QuizIcon />} iconPosition='start' />}
        </Tabs>

        {/* Content Tab */}
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ px: 3 }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <Typography variant='h4' component='h1' gutterBottom>
                    {children}
                  </Typography>
                ),
                h2: ({ children }) => (
                  <Typography variant='h5' component='h2' gutterBottom sx={{ mt: 3 }}>
                    {children}
                  </Typography>
                ),
                h3: ({ children }) => (
                  <Typography variant='h6' component='h3' gutterBottom sx={{ mt: 2 }}>
                    {children}
                  </Typography>
                ),
                p: ({ children }) => (
                  <Typography variant='body1' paragraph>
                    {children}
                  </Typography>
                ),
                code: ({ children, className }) => {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  const isInline = !className;

                  if (isInline) {
                    return (
                      <Box
                        component='code'
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          bgcolor: 'grey.100',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        {children}
                      </Box>
                    );
                  }

                  // Handle Mermaid diagrams
                  if (language === 'mermaid') {
                    return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
                  }

                  return (
                    <Box sx={{ my: 2 }}>
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={language || 'python'}
                        PreTag='div'
                        customStyle={{
                          borderRadius: '8px',
                          fontSize: '14px',
                          lineHeight: '1.5',
                        }}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </Box>
                  );
                },
                ul: ({ children }) => (
                  <Box component='ul' sx={{ pl: 3, mb: 2 }}>
                    {children}
                  </Box>
                ),
                li: ({ children }) => (
                  <Typography component='li' variant='body1' sx={{ mb: 0.5 }}>
                    {children}
                  </Typography>
                ),
                table: ({ children }) => (
                  <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <Box
                      component='table'
                      sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        border: 1,
                        borderColor: 'grey.300',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {children}
                    </Box>
                  </Box>
                ),
                thead: ({ children }) => (
                  <Box
                    component='thead'
                    sx={{
                      bgcolor: 'grey.50',
                    }}
                  >
                    {children}
                  </Box>
                ),
                tbody: ({ children }) => <Box component='tbody'>{children}</Box>,
                tr: ({ children }) => (
                  <Box
                    component='tr'
                    sx={{
                      '&:nth-of-type(even)': {
                        bgcolor: 'grey.50',
                      },
                    }}
                  >
                    {children}
                  </Box>
                ),
                th: ({ children }) => (
                  <Box
                    component='th'
                    sx={{
                      border: 1,
                      borderColor: 'grey.300',
                      px: 2,
                      py: 1,
                      textAlign: 'left',
                      fontWeight: 'bold',
                    }}
                  >
                    <Typography variant='body2' component='span' sx={{ fontWeight: 'bold' }}>
                      {children}
                    </Typography>
                  </Box>
                ),
                td: ({ children }) => (
                  <Box
                    component='td'
                    sx={{
                      border: 1,
                      borderColor: 'grey.300',
                      px: 2,
                      py: 1,
                      verticalAlign: 'top',
                    }}
                  >
                    <Typography variant='body2' component='span'>
                      {children}
                    </Typography>
                  </Box>
                ),
              }}
            >
              {module.content}
            </ReactMarkdown>
          </Box>
        </TabPanel>

        {/* Exercises Tab */}
        <TabPanel value={currentTab} index={1}>
          <Box sx={{ px: 3 }}>
            {module.exercises.map((exercise, index) => {
              const exerciseProgress = progress?.exerciseProgress[exercise.id];
              const isExerciseCompleted = exerciseProgress?.completed || false;

              return (
                <Card key={exercise.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant='h6' sx={{ flexGrow: 1 }}>
                        Exercise {index + 1}: {exercise.title}
                      </Typography>
                      <Chip
                        label={exercise.difficulty}
                        size='small'
                        color={
                          exercise.difficulty === 'easy'
                            ? 'success'
                            : exercise.difficulty === 'medium'
                              ? 'warning'
                              : 'error'
                        }
                        sx={{ mr: 1 }}
                      />
                      {isExerciseCompleted && (
                        <Chip label='Completed' color='success' size='small' />
                      )}
                    </Box>

                    <Typography variant='body2' color='text.secondary' paragraph>
                      {exercise.description}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Chip label={exercise.type} variant='outlined' size='small' />
                      <Button
                        variant={isExerciseCompleted ? 'outlined' : 'contained'}
                        onClick={() => handleExerciseClick(exercise.id)}
                      >
                        {isExerciseCompleted ? 'Review' : 'Start Exercise'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </TabPanel>

        {/* Quiz Tab */}
        {module.quiz && (
          <TabPanel value={currentTab} index={2}>
            <Box sx={{ px: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant='h6' gutterBottom>
                    {module.quiz.title}
                  </Typography>

                  <Typography variant='body2' color='text.secondary' paragraph>
                    Test your knowledge with {module.quiz.questions.length} questions. You need to
                    score at least {module.quiz.passingScore} out of {module.quiz.questions.length}{' '}
                    to pass.
                  </Typography>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Chip label={`${module.quiz.questions.length} questions`} variant='outlined' />
                    <Button variant='contained' onClick={handleQuizClick}>
                      Take Quiz
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </TabPanel>
        )}
      </Paper>

      {/* Navigation and Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant='outlined'
          startIcon={<PrevIcon />}
          onClick={handlePrevModule}
          disabled={!prevModule}
        >
          {prevModule ? `Previous: ${prevModule.title}` : 'Previous Module'}
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isCompleted && completionPercentage === 100 && (
            <Button variant='contained' color='success' onClick={handleCompleteModule}>
              Mark as Complete
            </Button>
          )}
        </Box>

        <Button
          variant='outlined'
          endIcon={<NextIcon />}
          onClick={handleNextModule}
          disabled={!nextModule}
        >
          {nextModule ? `Next: ${nextModule.title}` : 'Next Module'}
        </Button>
      </Box>
    </Container>
  );
};

export default ModuleViewer;
